import { api } from './api';
import { Product } from '../types';

export const productsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: builder.query<{ success: boolean; products: Product[]; pagination: any }, {
      page?: number;
      limit?: number;
      search?: string;
      category?: string;
      active?: boolean;
    }>({
      query: (params = {}) => {
        const searchParams = new URLSearchParams();
        
        if (params.page) searchParams.append('page', params.page.toString());
        if (params.limit) searchParams.append('limit', params.limit.toString());
        if (params.search) searchParams.append('search', params.search);
        if (params.category) searchParams.append('category', params.category);
        if (params.active !== undefined) searchParams.append('active', params.active.toString());
        
        return {
          url: `/products?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Product'],
    }),
    
    quickSearchProducts: builder.query<{ success: boolean; products: Product[]; total: number }, {
      q: string;
      limit?: number;
    }>({
      query: ({ q, limit = 20 }) => {
        const searchParams = new URLSearchParams();
        searchParams.append('q', q);
        searchParams.append('limit', limit.toString());
        
        return {
          url: `/products/quick-search?${searchParams.toString()}&_t=${Date.now()}`,
          method: 'GET',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        };
      },
      providesTags: ['Product'],
      keepUnusedDataFor: 0, // No cache for fresh inventory data
    }),
    
    searchProductsWithPattern: builder.query<{ success: boolean; data: Product[]; total: number; pattern: string }, {
      pattern: string;
      limit?: number;
    }>({
      query: ({ pattern, limit = 50 }) => {
        const searchParams = new URLSearchParams();
        searchParams.append('pattern', pattern);
        searchParams.append('limit', limit.toString());
        
        return {
          url: `/products/search-pattern?${searchParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Product'],
    }),
    
    getProductByBarcode: builder.query<{ success: boolean; data: Product }, string>({
      query: (barcode) => ({
        url: `/products/barcode/${barcode}`,
        method: 'GET',
      }),
      providesTags: ['Product'],
    }),
    
    getProduct: builder.query<{ success: boolean; data: Product }, string>({
      query: (id) => ({
        url: `/products/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Product', id }],
    }),
    
    createProduct: builder.mutation<{ success: boolean; data: Product }, Partial<Product> & { locationId?: string; initialQuantity?: number }>({
      query: (productData) => ({
        url: '/products',
        method: 'POST',
        body: productData,
      }),
      invalidatesTags: ['Product'],
    }),
    
    updateProduct: builder.mutation<{ success: boolean; data: Product }, { id: string; data: Partial<Product> }>({
      query: ({ id, data }) => ({
        url: `/products/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'Product', id },
        'Product' // Invalidate the general product list to refresh the UI
      ],
    }),
    
    deleteProduct: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/products/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Product', id }],
    }),
    
    // Validar código de barras
    validateBarcode: builder.query<{
      success: boolean;
      data: {
        barcode: string;
        isUnique: boolean;
        existingProduct: { id: string; name: string; barcode: string } | null;
      };
    }, { barcode: string; excludeId?: string }>({
      query: ({ barcode, excludeId }) => ({
        url: `/products/validate-barcode/${barcode}${excludeId ? `?excludeId=${excludeId}` : ''}`,
        method: 'GET',
      }),
      providesTags: ['Product'],
    }),

    // Product movements tracking
    getProductMovements: builder.query<{
      success: boolean;
      product: {
        id: string;
        name: string;
        sku: string;
        barcode: string;
        alternativeBarcodes: string[];
        alternativeCode?: string;
        category: string;
        unit: string;
        salePrice: number;
        currentStock: number;
      } | null;
      movements: Array<{
        id: string;
        type: 'SALE' | 'TRANSFER_OUT' | 'TRANSFER_IN' | 'ADJUSTMENT' | 'PURCHASE' | 'INITIAL_STOCK';
        quantity: number;
        unitPrice?: number;
        total?: number;
        reason?: string;
        referenceId?: string;
        createdAt: string;
        user: {
          name: string;
          employeeCode?: string;
        };
        location: {
          name: string;
        };
        relatedLocation?: {
          name: string;
        };
      }>;
      message?: string;
    }, { search: string }>({
      query: (params) => ({
        url: `/products/movements?search=${encodeURIComponent(params.search)}`,
        method: 'GET',
      }),
      providesTags: ['Product'],
    }),

    // Generar código de barras único
    generateUniqueBarcode: builder.mutation<{
      success: boolean;
      data: {
        barcode: string;
        category: string;
        attempts: number;
      };
    }, { category: string }>({
      query: (data) => ({
        url: '/products/generate-barcode',
        method: 'POST',
        body: data,
      }),
    }),
  }),
});

export const {
  useGetProductsQuery,
  useQuickSearchProductsQuery,
  useSearchProductsWithPatternQuery,
  useGetProductByBarcodeQuery,
  useGetProductQuery,
  useGetProductMovementsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useValidateBarcodeQuery,
  useGenerateUniqueBarcodeMutation,
} = productsApi;